import type { Article } from './articleTypes';

export const motorisations: Article = {
  slug: 'essence-diesel-hybride-electrique',
  category: 'Motorisations',
  title: 'Hybride, diesel, essence ou électrique : quelle motorisation choisir ?',
  seoTitle: 'Hybride ou diesel ? Essence ? Que choisir selon vos trajets',
  description:
    'Hybride ou diesel, essence ou hybride : comparatif indépendant selon vos trajets en ville ou sur autoroute, la recharge, le Crit’Air et le coût global.',
  intro:
    'La meilleure motorisation n’existe pas sans trajet. Une hybride peut être remarquable en ville et quelconque sur autoroute ; un diesel peut rester cohérent pour un gros rouleur et devenir pénible sur une succession de petits parcours.',
  image: '/assets/guides/guide-motorisations.webp',
  imageAlt:
    'Quatre voitures vues du dessus, alignées dans des zones de comparaison bleues',
  publishedAt: '2026-07-17',
  updatedAt: '2026-10-22',
  readingTime: '16 min',
  takeaways: [
    'Hybride ou diesel : la full hybrid est à l’aise quand le rythme varie (ville, périurbain) ; le diesel garde sa logique pour de longs trajets réguliers à vitesse stabilisée.',
    'La première question n’est pas le kilométrage annuel, mais la forme des trajets : distance, vitesse, fréquence et possibilité de recharge.',
    'Une hybride rechargeable sans recharge régulière perd l’essentiel de sa logique.',
    'Pour comparer le coût, utilisez votre consommation probable et votre prix d’énergie, puis ajoutez achat, assurance, entretien et dépréciation.',
  ],
  sections: [
    {
      id: 'hybride-ou-diesel-autoroute',
      title: 'Hybride ou diesel : lequel choisir, notamment sur autoroute ?',
      content: (
        <>
          <p>
            Ma réponse courte : pour un quotidien fait de ville, de périurbain et de vitesses
            variables, je regarderais d’abord une hybride non rechargeable. Pour de longs trajets
            réguliers, majoritairement sur autoroute, je garderais un diesel récent dans la
            comparaison. Entre les deux, c’est le coût global sur votre durée de détention qui
            tranche, pas l’étiquette.
          </p>
          <div className="as-table-scroll" role="region" aria-label="Hybride ou diesel selon le trajet" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>Votre usage principal</th>
                  <th>Hybride non rechargeable</th>
                  <th>Diesel récent</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Ville, bouchons, petits trajets</th>
                  <td>Terrain favorable : récupération d’énergie et phases électriques fréquentes.</td>
                  <td>À éviter en usage dominant : moteur et dépollution travaillent mal sur parcours courts.</td>
                </tr>
                <tr>
                  <th>Trajets mixtes</th>
                  <td>Souvent cohérente, à comparer avec une essence de même gabarit.</td>
                  <td>Défendable seulement si la part de route et d’autoroute reste importante.</td>
                </tr>
                <tr>
                  <th>Autoroute presque quotidienne</th>
                  <td>L’écart se resserre : moins d’occasions de récupérer de l’énergie.</td>
                  <td>Garde sa logique grâce à son rendement à vitesse stabilisée et à son autonomie.</td>
                </tr>
                <tr>
                  <th>Accès aux ZFE</th>
                  <td>Une hybride essence Euro 5 ou 6 est classée Crit’Air 1.</td>
                  <td>Un diesel Euro 5 ou 6 est classé Crit’Air 2 : vérifier les règles locales.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Pour des trajets majoritairement autoroutiers et longs, je comparerais d’abord la
            consommation du modèle sur autoroute, son prix et son historique d’entretien.
            Une moyenne urbaine favorable à l’hybride ne permet pas de prédire votre facture
            à vitesse stabilisée. À l’inverse, rouler sur autoroute quelques week-ends ne
            suffit pas à justifier un diesel pour toute l’année.
          </p>
          <ul>
            <li><strong>Autoroute presque quotidienne :</strong> garder un diesel adapté dans la sélection et le comparer à une essence ou une hybride de gabarit similaire.</li>
            <li><strong>Ville la semaine, autoroute en vacances :</strong> donner plus de poids au trajet ordinaire ; une full hybrid mérite de rester dans la comparaison.</li>
            <li><strong>Petits parcours répétés :</strong> ne pas choisir le diesel sur son seul chiffre de consommation annoncé.</li>
          </ul>
          <h3>Hybride rechargeable ou diesel ?</h3>
          <p>
            La comparaison n’a de sens que si la voiture peut être branchée très régulièrement.
            Sans recharge fiable, un hybride rechargeable roule surtout sur son moteur essence en
            transportant sa batterie ; pour un gros rouleur autoroutier sans prise, je ne le
            placerais pas devant un diesel. Le détail figure plus bas, dans
            {' '}<a href="#hybrides">les trois hybrides que le même mot mélange</a>.
          </p>
          <p>
            Mon avis : vérifier aussi l’agrément en charge, le bruit, les contraintes locales
            et le coût de détention. Il n’existe pas de vainqueur universel « hybride contre
            diesel ». L’<a href="/outils/orientation-motorisation/">outil d’orientation de motorisation</a>
            {' '}permet de partir de vos kilomètres, de vos trajets et de votre recharge avant
            de sélectionner des modèles.
          </p>
        </>
      ),
    },
    {
      id: 'essence-ou-hybride',
      title: 'Essence ou hybride : que choisir pour vos trajets quotidiens ?',
      content: (
        <>
          <p>
            Mon point de départ : comparer une essence et une hybride non rechargeable de taille
            similaire, avec le même budget total. Pour beaucoup de ville et de circulation avec
            arrêts fréquents, je regarderais d’abord le full hybrid. Pour un faible kilométrage
            et un prix d’achat contenu, je garderais une essence légère dans la comparaison.
            Ce sont des orientations de conseil, pas une garantie d’économie.
          </p>
          <p>
            Le mot « hybride » ne suffit pas : une micro-hybride, une full hybrid et une hybride
            rechargeable répondent à des usages différents. Sans prise régulière, je ne ferais
            pas du rechargeable le premier choix. Le guide sur
            {' '}<a href="/conseils/hybride-rechargeable-sans-recharge-domicile/">l’hybride rechargeable sans recharge à domicile</a>
            {' '}détaille cette distinction.
          </p>
          <h3>Comparer le surcoût plutôt que chercher un kilométrage magique</h3>
          <p>
            Exemple purement hypothétique : si l’hybride coûte 2 000 € de plus et économise
            400 € de carburant par an, il faut cinq ans pour compenser ce seul écart d’achat.
            Ce calcul exclut financement, assurance, entretien et différence de revente ; il
            ne suffit donc pas à désigner la voiture la moins chère. Remplacez ces hypothèses
            par celles de vos deux annonces dans le
            {' '}<a href="/outils/comparateur-cout-global/">comparateur de coût global automobile</a>.
          </p>
        </>
      ),
    },
    {
      id: 'questions',
      title: 'Les cinq questions qui éliminent déjà de mauvaises options',
      content: (
        <>
          <ol className="as-article-steps">
            <li>
              <strong>Combien de kilomètres fait un trajet ordinaire ?</strong>
              <span>Dix parcours de 3 km ne sollicitent pas une voiture comme un trajet de 30 km.</span>
            </li>
            <li>
              <strong>Quelle part se déroule en ville, sur route et sur autoroute ?</strong>
              <span>La récupération d’énergie d’un hybride aide surtout quand la vitesse varie.</span>
            </li>
            <li>
              <strong>Une recharge fiable existe-t-elle au domicile ou au travail ?</strong>
              <span>Une borne publique occasionnelle n’équivaut pas à une solution quotidienne.</span>
            </li>
            <li>
              <strong>Quels longs trajets faut-il réellement couvrir ?</strong>
              <span>On ne dimensionne pas forcément la voiture de toute l’année sur deux départs en vacances.</span>
            </li>
            <li>
              <strong>Combien de temps la voiture sera-t-elle conservée ?</strong>
              <span>Le prix d’achat et la dépréciation ne pèsent pas de la même façon sur trois ou huit ans.</span>
            </li>
          </ol>
          <p>
            Je demande ensuite les contraintes locales. Le classement Crit’Air dépend de l’énergie,
            de la norme Euro et de la date de première immatriculation. Les restrictions des zones à
            faibles émissions varient d’une agglomération à l’autre et peuvent évoluer : il faut vérifier
            la règle du territoire fréquenté, pas se fier à une phrase générale lue plusieurs années plus tôt.
          </p>
        </>
      ),
    },
    {
      id: 'comparatif',
      title: 'Le comparatif en une page',
      content: (
        <>
          <div className="as-table-scroll" role="region" aria-label="Comparaison des motorisations" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>Motorisation</th>
                  <th>Terrain favorable</th>
                  <th>Condition à réunir</th>
                  <th>Point de vigilance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Essence</th>
                  <td>Faible ou moyen kilométrage, trajets variés.</td>
                  <td>Choisir un moteur adapté au poids et à l’autoroute prévue.</td>
                  <td>Consommation en ville et sur gros SUV.</td>
                </tr>
                <tr>
                  <th>Diesel</th>
                  <td>Longues distances régulières, route et autoroute.</td>
                  <td>Des trajets assez longs pour mettre la mécanique en température.</td>
                  <td>Restrictions locales, système antipollution et revente.</td>
                </tr>
                <tr>
                  <th>Micro-hybride</th>
                  <td>Usage essence classique avec une assistance légère.</td>
                  <td>Ne pas la confondre avec un full hybrid.</td>
                  <td>Gain variable et conduite électrique généralement très limitée.</td>
                </tr>
                <tr>
                  <th>Hybride non rechargeable</th>
                  <td>Ville, périurbain, trafic et trajets mixtes.</td>
                  <td>Accepter une transmission et des sensations propres au modèle.</td>
                  <td>Avantage moindre à vitesse autoroutière stabilisée.</td>
                </tr>
                <tr>
                  <th>Hybride rechargeable</th>
                  <td>Trajets quotidiens électriques, longs trajets ponctuels.</td>
                  <td>Recharger presque chaque jour.</td>
                  <td>Poids, double chaîne de traction et consommation batterie vide.</td>
                </tr>
                <tr>
                  <th>Électrique</th>
                  <td>Quotidien prévisible, recharge simple, ville et périurbain.</td>
                  <td>Dimensionner autonomie et recharge sur l’usage majoritaire.</td>
                  <td>Prix des recharges rapides, longs trajets et taille de batterie.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <aside className="as-article-note as-note-fact">
            <strong>Fait réglementaire</strong>
            <p>
              Le site officiel Crit’Air classe les voitures 100 % électriques et hydrogène en catégorie 0,
              les hybrides rechargeables et les essence Euro 5/6 en catégorie 1, et les diesel Euro 5/6 en
              catégorie 2. Pour une voiture précise, la simulation officielle à partir de la carte grise
              reste la référence.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'essence-diesel',
      title: 'Essence ou diesel : le kilométrage ne suffit pas',
      content: (
        <>
          <h3>Essence : le choix polyvalent, surtout à kilométrage contenu</h3>
          <p>
            Une essence récente convient bien aux conducteurs qui roulent modérément, alternent petits
            et moyens trajets et ne veulent pas organiser leur usage autour d’une recharge. Sur une petite
            voiture légère, c’est souvent le choix le plus simple à acheter. Sur un SUV lourd utilisé en
            ville, l’addition à la pompe peut en revanche devenir sensible.
          </p>
          <p>
            Je ne valide jamais un moteur sur sa seule cylindrée. Le poids du véhicule, le rapport de boîte,
            la charge, le relief et la vitesse habituelle changent l’agrément et la consommation. Un petit
            moteur très sollicité sur autoroute peut être moins cohérent qu’une motorisation légèrement plus
            puissante utilisée sans effort constant.
          </p>
          <h3>Diesel : encore pertinent pour certains gros rouleurs</h3>
          <p>
            Un diesel moderne peut rester rationnel pour 20 000 à 30 000 km annuels composés majoritairement
            de longs trajets. Son rendement à vitesse stabilisée et son autonomie restent utiles. En revanche,
            je l’écarte d’une succession de parcours urbains courts : le moteur et les dispositifs de
            dépollution y travaillent dans des conditions défavorables.
          </p>
          <p>
            Il faut aussi intégrer l’accès aux zones fréquentées. Un diesel Euro 5 ou 6 est Crit’Air 2,
            quand une essence Euro 5 ou 6 est Crit’Air 1. Cette différence ne dit pas quelle voiture est
            économiquement la meilleure ; elle ajoute une contrainte possible au cahier des charges.
          </p>
        </>
      ),
    },
    {
      id: 'hybrides',
      title: 'Les trois hybrides que le même mot mélange',
      content: (
        <>
          <h3>Micro-hybride : une essence assistée</h3>
          <p>
            Un système 12 ou 48 volts récupère de l’énergie et aide le moteur thermique à certains moments.
            Sur la plupart des modèles, il ne permet pas de rouler durablement en électrique. Il peut réduire
            un peu la consommation, mais il ne procure ni le fonctionnement d’une Yaris hybride, ni celui
            d’une électrique. Je le traite comme une caractéristique du moteur essence, pas comme une
            catégorie miracle.
          </p>
          <h3>Full hybrid : particulièrement à l’aise quand le rythme varie</h3>
          <p>
            L’hybride non rechargeable peut entraîner la voiture avec le moteur électrique seul sur de
            courtes phases, récupérer de l’énergie au freinage et couper souvent le thermique. Ce cycle
            correspond bien à la ville et au périurbain. Sur autoroute stabilisée, les occasions de récupérer
            et réutiliser l’énergie diminuent ; le moteur essence travaille davantage et l’écart se resserre.
          </p>
          <h3>Hybride rechargeable : pertinent seulement branché</h3>
          <p>
            Le PHEV ajoute une batterie capable de couvrir plusieurs dizaines de kilomètres et conserve un
            moteur thermique. IFPEN souligne que ses émissions réelles dépendent fortement de la fréquence de
            recharge et de la distance parcourue en électrique. Le glossaire Car Labelling de l’ADEME le dit
            sans détour : son intérêt environnemental exige une recharge systématique ou très régulière.
          </p>
          <aside className="as-article-note as-note-opinion">
            <strong>Ma règle simple</strong>
            <p>
              Si vous ne savez pas où la voiture sera branchée demain soir, je ne construis pas la shortlist
              autour d’un hybride rechargeable. Une borne hypothétique n’est pas une infrastructure de recharge.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'electrique',
      title: 'Électrique : partir de la recharge, pas de l’autonomie maximale',
      content: (
        <>
          <p>
            Une électrique devient très simple quand elle récupère chaque nuit ou chaque semaine l’énergie
            consommée au quotidien. Elle devient plus contraignante quand chaque recharge est une opération à
            planifier. Je commence donc par le stationnement, la puissance disponible, le tarif et la fiabilité
            de la solution, avant de regarder les kilomètres WLTP.
          </p>
          <p>
            Ensuite seulement, je dimensionne la batterie. L’ADEME recommande une capacité adaptée à l’usage
            majoritaire et rappelle qu’un véhicule plus petit et plus léger tire davantage de chaque kWh.
            Acheter une batterie surdimensionnée pour deux journées exceptionnelles augmente le prix, le poids
            et l’impact de fabrication toute l’année.
          </p>
          <p>
            Sur le cycle de vie complet en France, l’ADEME estime qu’une petite électrique à batterie raisonnable
            présente un impact carbone nettement inférieur à son équivalent thermique. Cette conclusion ne
            signifie pas « zéro impact » : fabrication de la batterie, poids du véhicule, source d’électricité,
            durée de vie et kilométrage restent déterminants.
          </p>
          <aside className="as-article-note as-note-fact">
            <strong>Fait, pas slogan</strong>
            <p>
              Une voiture électrique n’émet pas de gaz d’échappement à l’usage, mais sa fabrication et
              l’électricité consommée ont un impact. La bonne comparaison porte sur le cycle de vie et sur
              des véhicules de taille équivalente.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'calcul-cout',
      title: 'Calculer le coût d’énergie avec ses propres données',
      content: (
        <>
          <p>
            Les prix de l’essence et de l’électricité changent. Plutôt que de publier un résultat qui sera
            faux dans quelques mois, voici le calcul à refaire avec vos factures et la consommation observée
            du véhicule ciblé.
          </p>
          <div className="as-formula-grid">
            <div>
              <strong>Thermique ou hybride</strong>
              <code>km/an × (L/100 km ÷ 100) × €/L</code>
            </div>
            <div>
              <strong>Électrique</strong>
              <code>km/an × (kWh/100 km ÷ 100) × €/kWh</code>
            </div>
          </div>
          <p>
            Pour l’électrique, j’ajoute une hypothèse de pertes de recharge et je sépare les kWh chargés à
            domicile de ceux achetés sur borne rapide. Pour un PHEV, je calcule deux consommations : la part
            électrique réellement rechargeable et la part essence, batterie basse. Une moyenne officielle
            unique masque souvent ce partage.
          </p>
          <p>
            Enfin, l’énergie n’est qu’une ligne. Le <strong>coût global</strong> comprend aussi prix d’achat,
            financement, dépréciation estimée, assurance, entretien, pneus, fiscalité et stationnement. Une
            économie de carburant ne compense pas automatiquement un surcoût d’achat important.
          </p>
        </>
      ),
    },
    {
      id: 'cas-usage',
      title: 'La motorisation que je regarderais selon l’usage',
      content: (
        <>
          <dl className="as-article-decisions">
            <div>
              <dt>8 000 km/an, ville et petits trajets, aucune prise</dt>
              <dd>Essence légère ou full hybrid, selon le budget et le souhait de boîte automatique.</dd>
            </div>
            <div>
              <dt>12 000 km/an, domicile-travail de 35 km, recharge à domicile</dt>
              <dd>Électrique dimensionnée sur la semaine ordinaire ; essence ou hybride en comparaison.</dd>
            </div>
            <div>
              <dt>22 000 km/an, principalement autoroute</dt>
              <dd>Essence efficiente ou diesel récent selon le véhicule, le coût et les contraintes locales.</dd>
            </div>
            <div>
              <dt>50 km quotidiens, recharge facile, longs trajets fréquents</dt>
              <dd>Électrique si le réseau de recharge convient ; PHEV seulement avec discipline de branchement.</dd>
            </div>
            <div>
              <dt>Usage imprévisible et stationnement dans la rue</dt>
              <dd>Essence ou full hybrid : garder une énergie facile à obtenir peut valoir plus qu’un gain théorique.</dd>
            </div>
          </dl>
          <p>
            Ces orientations sont des points de départ. Le poids et l’aérodynamique peuvent compter autant que
            l’étiquette : une petite essence sobre peut consommer moins qu’un gros hybride, et une compacte
            électrique à batterie raisonnable moins qu’un SUV électrique surdimensionné.
          </p>
          <p>
            Pour passer de la technologie aux modèles, voyez les sélections <a href="/conseils/quelle-citadine-15000-euros/">
            de citadines à 15 000 €</a> et <a href="/conseils/quel-suv-30000-euros/">de SUV à 30 000 €</a>.
          </p>
        </>
      ),
    },
  ],
  sources: [
    {
      title: 'Classement officiel des véhicules Crit’Air',
      publisher: 'Ministère de la Transition écologique',
      url: 'https://www.certificat-air.gouv.fr/',
    },
    {
      title: 'Zones à faibles émissions et restrictions en vigueur',
      publisher: 'Ministère de la Transition écologique',
      url: 'https://www.ecologie.gouv.fr/politiques-publiques/zones-faibles-emissions-zfe',
    },
    {
      title: 'Glossaire des motorisations et de l’hybride rechargeable',
      publisher: 'Car Labelling ADEME',
      url: 'https://carlabelling.ademe.fr/index/glossaire',
    },
    {
      title: 'Une batterie de taille raisonnable assure une pertinence climatique et économique',
      publisher: 'ADEME',
      url: 'https://www.ademe.fr/presse/communique-national/mondial-de-lautomobile-lademe-publie-son-avis-sur-le-vehicule-electrique-une-batterie-de-taille-raisonnable-assure-une-pertinence-climatique-et-economique',
    },
    {
      title: 'La voiture électrique est-elle plus polluante que la thermique ?',
      publisher: 'ADEME Infos',
      url: 'https://infos.ademe.fr/magazine-juillet-2026/la-voiture-electrique-est-plus-polluante-que-la-voiture-thermique/',
    },
    {
      title: 'Évaluer en conditions réelles les émissions des hybrides rechargeables',
      publisher: 'IFP Énergies nouvelles',
      url: 'https://www.ifpenergiesnouvelles.fr/article/evaluer-en-conditions-reelles-les-emissions-des-vehicules-hybrides-rechargeables',
    },
    {
      title: 'Calculateur d’émissions carbone des trajets',
      publisher: 'ADEME',
      url: 'https://agirpourlatransition.ademe.fr/particuliers/evaluer-son-impact/calculer-empreinte-carbone/calculer-emissions-carbone-trajets',
    },
  ],
  faqs: [
    {
      question: 'Hybride ou diesel : lequel consomme le moins ?',
      answer:
        'Cela dépend du trajet. En ville et en circulation variable, une hybride non rechargeable tire parti de la récupération d’énergie. Sur autoroute à vitesse stabilisée, cet avantage se réduit et un diesel peut rester très sobre. Comparez les consommations des deux modèles précis sur votre type de trajet, puis le coût global.',
    },
    {
      question: 'Quelle motorisation choisir pour 10 000 km par an ?',
      answer:
        'Le kilométrage seul ne suffit pas. Sans recharge et avec des trajets courts, une essence légère ou une hybride non rechargeable sont souvent cohérentes. Avec une recharge simple et un quotidien prévisible, une électrique peut l’être davantage.',
    },
    {
      question: 'À partir de combien de kilomètres un diesel devient-il rentable ?',
      answer:
        'Il n’existe pas de seuil universel. Il faut comparer le surcoût d’achat, la consommation réelle, l’entretien, l’assurance, la durée de détention et les restrictions locales. La nature routière des trajets compte autant que le total annuel.',
    },
    {
      question: 'Une hybride rechargeable est-elle intéressante sans borne à domicile ?',
      answer:
        'Rarement si aucune recharge régulière n’est possible ailleurs. Batterie basse, elle conserve le poids et la complexité de ses deux chaînes de traction tout en consommant du carburant. Une borne publique fiable au travail peut toutefois changer le calcul.',
    },
    {
      question: 'Une voiture électrique est-elle vraiment moins polluante ?',
      answer:
        'En France, l’ADEME conclut à un avantage sur le cycle de vie pour une électrique de taille et de batterie raisonnables comparée à une thermique équivalente. Cet avantage varie avec le poids, la batterie, le kilométrage, la production électrique et la durée de vie.',
    },
  ],
  relatedSlugs: [
    'quelle-citadine-15000-euros',
    'quel-suv-30000-euros',
    'achat-credit-loa-lld',
  ],
};
